import { useContentfulLiveUpdates } from "@contentful/live-preview/react"
import { ReactSVG } from "react-svg"
import classNames from "classnames"

// -------------------------------------------------------

import { getFeaturedPeoplePaths, getPersonData } from "../../lib/api"

import Layout from "../../layout"
import CardGrid from "@templates/card-grid"

import ContentBlock from "@components/content"
import Image from "@components/image"

// -------------------------------------------------------

import {
  card_grid_header,
  has_description,
  person_container,
  person_bio,
  person_description,
  person_header,
  share_icons
} from "./styles.module.scss"

// -------------------------------------------------------

export async function getStaticPaths() {
  const slugs = (await getFeaturedPeoplePaths()) ?? []

  return {
    paths: slugs,
    fallback: "blocking"
  }
}

export async function getStaticProps(context) {
  const preview = process.env.NODE_ENV === "development" || context.draftMode || false
  const previewEnv = context?.previewData?.environment || null
  const person = (await getPersonData(context.params.slug, preview, previewEnv, 10)) ?? []

  if ((!person.name || person.linkedFrom?.entryCollection?.total < 2) && !person.isFeatured) {
    return {
      notFound: true,
      props: {}
    }
  }

  return {
    props: { person, preview }
  }
}

// -------------------------------------------------------

const People = (props) => {
  const { person, preview } = props
  const livePerson = useContentfulLiveUpdates(person)
  const { linkedIn } = livePerson

  const blogPosts = person.linkedFrom.blogPostCollection?.items
  const resourcePosts = livePerson.linkedFrom.resourceCollection?.items
  const webinarPosts = livePerson.linkedFrom.webinarResourceCollection?.items

  let filteredBlogPosts = blogPosts.filter((post) => post != null)
  filteredBlogPosts.forEach((post) => {
    post["type"] = "Blog"
    post["link"] = `/blog/${encodeURIComponent(post?.slug)}`
  })

  let filteredResourcePosts = resourcePosts.filter((post) => post != null)
  filteredResourcePosts.forEach((post) => {
    post["link"] = `/resources/${encodeURIComponent(post?.slug)}`
  })

  let filteredWebinarPosts = webinarPosts.filter((post) => post != null)
  filteredWebinarPosts.forEach((post) => {
    post["link"] = `/resources/webinars/${encodeURIComponent(post?.slug)}`
  })

  const resourceArr = [...filteredWebinarPosts, ...filteredResourcePosts]
    .sort(
      (a, b) =>
        new Date(b.date || b.sys.firstPublishedAt) - new Date(a.date || a.sys.firstPublishedAt)
    )
    .filter((post) => !post.isPrivate)

  const postArr = [...resourceArr, ...filteredBlogPosts]
    .sort(
      (a, b) =>
        new Date(b.date || b.sys.firstPublishedAt) - new Date(a.date || a.sys.firstPublishedAt)
    )
    .filter((post) => !post.isPrivate)

  const posts = { items: postArr }

  const metaDesc = livePerson.description
    ? `${livePerson.name}—${livePerson.description}`
    : undefined

  const classes = classNames(person_container, livePerson.biography ? has_description : null)

  return (
    <Layout pageTitle={livePerson.name} preview={preview} excerpt={metaDesc}>
      <div className={classes}>
        <div className={person_header}>
          {livePerson.image && (
            <Image
              src={person.image.url + "?fit=crop,center&crop=faces&ar=1:1&faceindex=1"}
              width={person.image.width}
              height={person.image.height}
              alt={person.image.description || person.name + person.description}
              sizes="170px"
            />
          )}
          <div>
            <h1>{person.name}</h1>
            {livePerson.description && (
              <p className={person_description}>{livePerson.description}</p>
            )}
            {linkedIn && (
              <div className={share_icons}>
                {linkedIn && (
                  <a href={`${linkedIn}`} target="_blank" rel="noreferrer noopener">
                    <ReactSVG src={`/icons/logo-linkedin.svg`} />
                  </a>
                )}
              </div>
            )}
          </div>
        </div>
        {livePerson.biography && (
          <ContentBlock className={person_bio} content={livePerson.biography} />
        )}
      </div>

      {posts.items.length > 0 && (
        <div className={card_grid_header}>
          <h2>{` Resources featuring ${livePerson.name}`}</h2>
        </div>
      )}
      <CardGrid data={posts} />
    </Layout>
  )
}

export default People
